import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { fetchBiancaData, computeMetrics, formatBRL } from './biancaDataService';

// ============ Styled ============
const Greeting = styled.div`
  margin-bottom: 32px;
  text-align: left;
  h1 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 38px;
    font-weight: 500;
    letter-spacing: -0.3px;
    color: ${p => p.theme.ink};
    margin: 0 0 4px;
  }
  p { color: ${p => p.theme.muted}; font-size: 14px; margin: 0; }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-bottom: 28px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, 1fr); }
`;
const Kpi = styled.div`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 22px 24px;
  position: relative;
  overflow: hidden;
  &::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: ${p => p.$danger ? p.theme.danger : p.theme.gold};
    opacity: ${p => p.$danger ? 1 : .6};
  }
`;
const KpiLabel = styled.div`
  font-size: 10px; letter-spacing: 2.5px; color: ${p => p.theme.muted};
  text-transform: uppercase; font-weight: 500;
`;
const KpiValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 42px; font-weight: 500; line-height: 1; margin-top: 8px;
  color: ${p => p.$danger ? p.theme.danger : p.theme.ink};
`;
const KpiTrend = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; margin-top: 10px; font-weight: 500;
  color: ${p => p.$up ? p.theme.ok : p.theme.danger};
`;
const KpiHint = styled.div`
  font-size: 12px; color: ${p => p.theme.muted}; margin-top: 8px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 22px;
  margin-bottom: 22px;
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
const Row3 = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 22px;
  margin-bottom: 22px;
  @media (max-width: 1200px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

const Panel = styled.section`
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  padding: 24px 26px;
`;
const PanelHead = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 18px;
  h2 {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 22px; font-weight: 600; color: ${p => p.theme.ink}; margin: 0;
  }
  a {
    font-size: 11px; letter-spacing: 1px; color: ${p => p.theme.gold};
    text-transform: uppercase; cursor: pointer; text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;
const HeadHint = styled.span`
  font-size: 11px; letter-spacing: 1px; text-transform: uppercase;
  color: ${p => p.$danger ? p.theme.danger : p.theme.muted};
  font-weight: ${p => p.$danger ? 600 : 500};
`;

const PeriodTabs = styled.div`
  display: flex; gap: 4px;
  button {
    border: none; background: transparent; font-size: 11px;
    padding: 4px 10px; border-radius: 5px;
    color: ${p => p.theme.muted}; cursor: pointer; font-family: inherit;
    letter-spacing: 0.5px; font-weight: 500;
  }
  button.active {
    background: ${p => p.theme.ink}; color: ${p => p.theme.bg};
  }
`;

// ===== Funil =====
const Funnel = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`;
const FunnelRow = styled.div`
  display: flex; align-items: center; gap: 14px;
  .label { width: 110px; font-size: 12px; color: ${p => p.theme.inkSoft}; }
  .bar {
    flex: 1; height: 28px; background: ${p => p.theme.surfaceAlt};
    border-radius: 6px; overflow: hidden;
  }
  .fill {
    height: 100%; border-radius: 6px;
    background: linear-gradient(90deg, ${p => p.theme.gold} 0%, ${p => p.theme.goldSoft} 100%);
    min-width: 2px;
  }
  .num { width: 60px; text-align: right; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 19px; font-weight: 600; color: ${p => p.theme.ink}; }
  .pct { width: 50px; text-align: right; font-size: 11px; color: ${p => p.theme.muted}; }
`;

// ===== Donut =====
const DonutWrap = styled.div`
  display: flex; flex-direction: column; gap: 18px;
  .donut { margin: 0 auto; width: 160px; height: 160px; position: relative; }
  .donut svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  .center {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    text-align: center;
  }
  .center .big { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 28px; font-weight: 600; color: ${p => p.theme.ink}; }
  .center .small { font-size: 10px; color: ${p => p.theme.muted}; letter-spacing: 1.5px; text-transform: uppercase; }
`;
const Legend = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  .item {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px;
    padding: 5px 0;
    border-bottom: 1px dashed ${p => p.theme.line};
  }
  .item:last-child { border-bottom: none; }
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .name { flex: 1; color: ${p => p.theme.inkSoft}; }
  .val { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 16px; color: ${p => p.theme.ink}; }
`;

// ===== Imóveis em destaque =====
const TopList = styled.div`
  display: flex; flex-direction: column;
`;
const TopItem = styled.div`
  display: grid; grid-template-columns: 50px 1fr auto; gap: 14px; align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${p => p.theme.line};
  &:last-child { border-bottom: none; }
`;
const TopThumb = styled.div`
  width: 50px; height: 50px;
  border-radius: 6px;
  background: ${p => p.theme.surfaceAlt};
  background-size: cover;
  background-position: center;
  background-image: ${p => p.$src ? `url(${p.$src})` : 'none'};
  border: 1px solid ${p => p.theme.line};
  display: flex; align-items: center; justify-content: center;
  color: ${p => p.theme.muted}; font-size: 18px;
`;
const TopInfo = styled.div`
  min-width: 0;
  .t { font-size: 13px; font-weight: 500; color: ${p => p.theme.ink}; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .m { font-size: 11px; color: ${p => p.theme.muted}; margin-top: 2px; }
`;
const TopLeads = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 19px; font-weight: 600; text-align: right;
  color: ${p => p.theme.ink};
  small {
    display: block; font-family: Inter; font-size: 9px;
    color: ${p => p.theme.muted}; letter-spacing: 1px; text-transform: uppercase; font-weight: 500;
  }
`;

// ===== Visitas =====
const VisitList = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`;
const Visit = styled.div`
  display: flex; gap: 14px; align-items: center;
  padding: 10px 12px;
  border: 1px solid ${p => p.theme.line};
  border-radius: 8px;
`;
const VisitTime = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 17px; font-weight: 600; line-height: 1; text-align: center; min-width: 50px;
  color: ${p => p.theme.ink};
  small {
    display: block; font-family: Inter; font-size: 10px;
    color: ${p => p.theme.muted}; letter-spacing: 1px; text-transform: uppercase; font-weight: 500; margin-top: 4px;
  }
`;
const VisitInfo = styled.div`
  flex: 1; min-width: 0;
  .who { font-size: 13px; font-weight: 500; color: ${p => p.theme.ink}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .what { font-size: 11px; color: ${p => p.theme.muted}; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;
const Tag = styled.span`
  font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase;
  padding: 3px 8px; border-radius: 999px;
  border: 1px solid ${p => p.$ok ? p.theme.ok : p.$danger ? p.theme.danger : p.theme.gold};
  color: ${p => p.$ok ? p.theme.ok : p.$danger ? p.theme.danger : p.theme.gold};
  white-space: nowrap;
  flex-shrink: 0;
`;

// ===== Conversas + Tasks =====
const ConvItem = styled.div`
  display: grid; grid-template-columns: 40px 1fr auto; gap: 12px; align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${p => p.theme.line};
  &:last-child { border-bottom: none; }
`;
const Avatar = styled.div`
  width: 40px; height: 40px;
  border-radius: 50%;
  background: ${p => p.theme.goldSoft};
  color: ${p => p.theme.gold};
  display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 14px;
`;
const TaskList = styled.div`
  display: flex; flex-direction: column;
`;
const TaskItem = styled.div`
  display: flex; gap: 10px; align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed ${p => p.theme.line};
  &:last-child { border-bottom: none; }
  input { accent-color: ${p => p.theme.gold}; cursor: pointer; }
  .t { flex: 1; font-size: 13px; color: ${p => p.theme.ink}; }
  .when { font-size: 10px; letter-spacing: 0.5px; text-transform: uppercase; font-weight: 600; }
  .when.danger { color: ${p => p.theme.danger}; }
  .when.ok { color: ${p => p.theme.muted}; }
`;

const Empty = styled.div`
  padding: 32px 0; text-align: center; color: ${p => p.theme.muted}; font-size: 13px;
`;

// ============ Helpers ============
const COLORS = ['#B8935A', '#1A1F2B', '#9A8F7E', '#D4C5A8', '#5C5040', '#8B7B65'];
const initialsOf = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
};
const formatDateShort = (d) => {
  if (!d) return '';
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dayStart = new Date(d); dayStart.setHours(0,0,0,0);
  if (dayStart.getTime() === today.getTime()) return 'hoje';
  if (dayStart.getTime() === tomorrow.getTime()) return 'amanhã';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};
const formatTime = (d) => d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h') : '';
const formatRelative = (hours) => {
  if (hours < 1) return 'agora';
  if (hours < 24) return `há ${Math.floor(hours)}h`;
  const d = Math.floor(hours / 24);
  if (d === 1) return 'ontem';
  return `${d} dias`;
};
const pickCover = (imagens) => {
  if (!Array.isArray(imagens)) return null;
  const VIDEO_RE = /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i;
  return imagens.find(u => u && !VIDEO_RE.test(u)) || null;
};

// ============ Component ============
export default function BiancaDashboard({ theme }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    fetchBiancaData()
      .then(d => { if (!canceled) setData(d); })
      .catch(e => { if (!canceled) setError(e.message || String(e)); })
      .finally(() => { if (!canceled) setLoading(false); });
    return () => { canceled = true; };
  }, []);

  const metrics = useMemo(() => data ? computeMetrics(data, period) : null, [data, period]);

  if (loading) return <Empty>Carregando dados da Bianca…</Empty>;
  if (error) return <Empty>Erro ao carregar: {error}</Empty>;
  if (!metrics) return <Empty>Sem dados.</Empty>;

  const { kpis, serieDias, totalPeriodo, mediaDia, pico, origens, funil, conversaoLeadVenda,
    imoveisDestaque, visitas, leadsQuentes, imoveisEncalhados, totalLeads, imoveisPublicados } = metrics;

  const greet = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();

  // ===== SVG: Area chart =====
  const W = 600, H = 200;
  const maxN = Math.max(1, ...serieDias.map(d => d.n));
  const stepX = serieDias.length > 1 ? W / (serieDias.length - 1) : W;
  const scaleY = (n) => H - 30 - (n / maxN) * (H - 50);
  const pathLine = serieDias.map((d, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${scaleY(d.n)}`).join(' ');
  const pathArea = `${pathLine} L ${(serieDias.length - 1) * stepX} ${H} L 0 ${H} Z`;
  const formatXLabel = (idx) => {
    const d = serieDias[idx]?.data;
    if (!d) return '';
    return `${d.slice(8)}/${d.slice(5,7)}`;
  };

  // ===== SVG: Donut =====
  const totalOrig = origens.reduce((s, o) => s + o.n, 0);
  let acc = 0;
  const donutSegs = origens.slice(0, 5).map((o, i) => {
    const pct = totalOrig ? (o.n / totalOrig) * 100 : 0;
    const seg = { ...o, pct, offset: -acc, color: COLORS[i] };
    acc += pct;
    return seg;
  });

  return (
    <>
      <Greeting>
        <h1>{greet}.</h1>
        <p>Resumo MF Imóveis · {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })} · {imoveisPublicados} imóveis publicados</p>
      </Greeting>

      {/* KPIs */}
      <KpiGrid>
        <Kpi>
          <KpiLabel>Leads no mês</KpiLabel>
          <KpiValue>{kpis.leadsNoMes}</KpiValue>
          {kpis.trendPct !== null ? (
            <KpiTrend $up={kpis.trendPct >= 0}>
              {kpis.trendPct >= 0 ? '↑' : '↓'} {Math.abs(kpis.trendPct)}% vs mês anterior
            </KpiTrend>
          ) : (
            <KpiHint>{kpis.leadsMesAnterior} no mês anterior</KpiHint>
          )}
        </Kpi>
        <Kpi>
          <KpiLabel>Conversão lead → visita</KpiLabel>
          <KpiValue>{kpis.conversaoMes}%</KpiValue>
          {kpis.conversaoTrend !== 0 ? (
            <KpiTrend $up={kpis.conversaoTrend >= 0}>
              {kpis.conversaoTrend >= 0 ? '↑' : '↓'} {Math.abs(kpis.conversaoTrend)}% vs mês anterior
            </KpiTrend>
          ) : (
            <KpiHint>estável vs mês anterior</KpiHint>
          )}
        </Kpi>
        <Kpi>
          <KpiLabel>Visitas próx. 7 dias</KpiLabel>
          <KpiValue>{kpis.visitasProx7}</KpiValue>
          <KpiHint>{kpis.visitasHoje} hoje · {kpis.visitasAmanha} amanhã</KpiHint>
        </Kpi>
        <Kpi>
          <KpiLabel>Ticket médio</KpiLabel>
          <KpiValue>{formatBRL(kpis.ticketMedio)}</KpiValue>
          <KpiHint>portfólio publicado</KpiHint>
        </Kpi>
      </KpiGrid>

      {/* Linha 1: chart + donut */}
      <Row>
        <Panel>
          <PanelHead>
            <h2>Leads ao longo do tempo</h2>
            <PeriodTabs>
              <button className={period === 7 ? 'active' : ''} onClick={() => setPeriod(7)}>7d</button>
              <button className={period === 30 ? 'active' : ''} onClick={() => setPeriod(30)}>30d</button>
              <button className={period === 90 ? 'active' : ''} onClick={() => setPeriod(90)}>90d</button>
            </PeriodTabs>
          </PanelHead>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 200 }}>
            <defs>
              <linearGradient id="bgGold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.gold} stopOpacity="0.45"/>
                <stop offset="100%" stopColor={theme.gold} stopOpacity="0"/>
              </linearGradient>
            </defs>
            <line x1="0" y1={scaleY(maxN)} x2={W} y2={scaleY(maxN)} stroke={theme.line} strokeDasharray="3 3" />
            <line x1="0" y1={scaleY(maxN/2)} x2={W} y2={scaleY(maxN/2)} stroke={theme.line} strokeDasharray="3 3" />
            <line x1="0" y1={scaleY(0)} x2={W} y2={scaleY(0)} stroke={theme.line} strokeDasharray="3 3" />
            <path d={pathArea} fill="url(#bgGold)" />
            <path d={pathLine} stroke={theme.gold} strokeWidth="2" fill="none" />
            <text x="0" y={H - 6} fontSize="10" fill={theme.muted}>{formatXLabel(0)}</text>
            <text x={W/2 - 14} y={H - 6} fontSize="10" fill={theme.muted}>{formatXLabel(Math.floor(serieDias.length/2))}</text>
            <text x={W - 30} y={H - 6} fontSize="10" fill={theme.muted}>hoje</text>
          </svg>
          <div style={{ display: 'flex', gap: 24, marginTop: 14, fontSize: 12, flexWrap: 'wrap' }}>
            <div><span style={{ color: theme.muted }}>Total no período:</span> <b style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 18, color: theme.ink }}>{totalPeriodo}</b></div>
            <div><span style={{ color: theme.muted }}>Pico:</span> <b style={{ color: theme.ink }}>{pico.n} leads{pico.data ? ` em ${pico.data.slice(8)}/${pico.data.slice(5,7)}` : ''}</b></div>
            <div><span style={{ color: theme.muted }}>Média:</span> <b style={{ color: theme.ink }}>{mediaDia}/dia</b></div>
          </div>
        </Panel>

        <Panel>
          <PanelHead>
            <h2>Origem dos leads</h2>
          </PanelHead>
          {origens.length === 0 ? <Empty>Sem dados</Empty> : (
            <DonutWrap>
              <div className="donut">
                <svg viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15.915" fill="none" stroke={theme.surfaceAlt} strokeWidth="3.5" />
                  {donutSegs.map((s, i) => (
                    <circle key={i} cx="18" cy="18" r="15.915" fill="none"
                      stroke={s.color} strokeWidth="3.5"
                      strokeDasharray={`${s.pct} 100`}
                      strokeDashoffset={s.offset} />
                  ))}
                </svg>
                <div className="center">
                  <div className="big">{totalOrig}</div>
                  <div className="small">leads total</div>
                </div>
              </div>
              <Legend>
                {donutSegs.map((s, i) => (
                  <div className="item" key={i}>
                    <span className="dot" style={{ background: s.color }} />
                    <span className="name">{s.label}</span>
                    <span className="val">{s.n}</span>
                  </div>
                ))}
              </Legend>
            </DonutWrap>
          )}
        </Panel>
      </Row>

      {/* Linha 2: Funil + Empreendimentos + Visitas */}
      <Row3>
        <Panel>
          <PanelHead>
            <h2>Funil de conversão</h2>
            <HeadHint>baseado no status atual</HeadHint>
          </PanelHead>
          <Funnel>
            {funil.map((f, i) => (
              <FunnelRow key={i}>
                <span className="label">{f.etapa}</span>
                <div className="bar"><div className="fill" style={{ width: `${Math.max(f.pct, 0)}%` }} /></div>
                <span className="num">{f.n}</span>
                <span className="pct">{f.pct}%</span>
              </FunnelRow>
            ))}
          </Funnel>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${theme.line}`, fontSize: 12, color: theme.muted }}>
            Lead → Venda: <b style={{ color: theme.gold, fontSize: 14 }}>{conversaoLeadVenda}%</b>
            {' · '}Total: <b style={{ color: theme.ink }}>{totalLeads}</b> leads
          </div>
        </Panel>

        <Panel>
          <PanelHead>
            <h2>Imóveis em destaque</h2>
            <a>Ver todos →</a>
          </PanelHead>
          {imoveisDestaque.length === 0 ? <Empty>Nenhum imóvel publicado</Empty> : (
            <TopList>
              {imoveisDestaque.map(im => {
                const cover = pickCover(im.imagens);
                const hasLink = im.bianca_product_id;
                return (
                  <TopItem key={im.id}>
                    <TopThumb $src={cover} />
                    <TopInfo>
                      <div className="t">{im.titulo || 'Sem título'}</div>
                      <div className="m">
                        {[
                          im.area_m2 && `${im.area_m2}m²`,
                          im.preco && formatBRL(im.preco),
                        ].filter(Boolean).join(' · ') || (im.bairro || '')}
                      </div>
                    </TopInfo>
                    <TopLeads>
                      {hasLink ? (im.leadsCount ?? 0) : '—'}
                      <small>{hasLink ? 'leads' : 'sem vínculo'}</small>
                    </TopLeads>
                  </TopItem>
                );
              })}
            </TopList>
          )}
        </Panel>

        <Panel>
          <PanelHead>
            <h2>Próximas visitas</h2>
            <a>Agenda →</a>
          </PanelHead>
          {visitas.length === 0 ? <Empty>Nenhuma visita próxima.</Empty> : (
            <VisitList>
              {visitas.map(v => (
                <Visit key={v.lead_id}>
                  <VisitTime>
                    {formatTime(v._agData)}
                    <small>{formatDateShort(v._agData)}</small>
                  </VisitTime>
                  <VisitInfo>
                    <div className="who">{v.nome || 'Sem nome'}</div>
                    <div className="what">{v.produto || v.telefone || ''}</div>
                  </VisitInfo>
                  <Tag $ok={v.status === 'agendado'}>{v.status === 'agendado' ? 'agendado' : 'confirmar'}</Tag>
                </Visit>
              ))}
            </VisitList>
          )}
        </Panel>
      </Row3>

      {/* Linha 3: Conversas + Follow-ups */}
      <Row>
        <Panel>
          <PanelHead>
            <h2>Leads quentes da semana</h2>
            <HeadHint $danger={kpis.leadsQuentesTotal > 0}>
              {kpis.leadsQuentesTotal > 0 ? `${kpis.leadsQuentesTotal} para contatar` : 'sem leads quentes'}
            </HeadHint>
          </PanelHead>
          {leadsQuentes.length === 0 ? <Empty>Nenhum lead quente no momento.</Empty> : (
            <div>
              {leadsQuentes.map(l => (
                <ConvItem key={l.lead_id}>
                  <Avatar>{initialsOf(l.nome)}</Avatar>
                  <TopInfo>
                    <div className="t">{l.nome || 'Sem nome'}{l.produto ? ` · ${l.produto}` : ''}</div>
                    <div className="m">{l.reasons.slice(0, 3).join(' · ')}</div>
                  </TopInfo>
                  <Tag $ok>{l.score} pts</Tag>
                </ConvItem>
              ))}
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHead>
            <h2>Imóveis encalhados</h2>
            <HeadHint $danger={kpis.imoveisEncalhadosTotal > 0}>
              {kpis.imoveisEncalhadosTotal > 0 ? `${kpis.imoveisEncalhadosTotal} a revisar` : 'catálogo em dia'}
            </HeadHint>
          </PanelHead>
          {imoveisEncalhados.length === 0 ? <Empty>Nenhum imóvel encalhado.</Empty> : (
            <TopList>
              {imoveisEncalhados.map(i => (
                <TopItem key={i.id}>
                  <TopThumb $src={pickCover(i.imagens)} />
                  <TopInfo>
                    <div className="t">{i.titulo || 'Sem título'}</div>
                    <div className="m">{[i.bairro, i.cidade].filter(Boolean).join(' · ') || '—'} · {formatBRL(i.preco)}</div>
                  </TopInfo>
                  <TopLeads>{i.idadeDias}<small>dias</small></TopLeads>
                </TopItem>
              ))}
            </TopList>
          )}
        </Panel>
      </Row>
    </>
  );
}
